import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Checkbox,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { IMedication, MEDICATION_STATUSES } from 'API';
import {
  CustomMetaTitle,
  LinkReturnToMain,
  Loader,
  NextPrevButton,
  MedicationListItem,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  useDictionary,
  useIsDesktop,
  useMedications,
  usePrescriptionAndUnHandledOrders,
} from 'Hooks';
import { OrderRegularMedicationsContext } from '../OrderRegularMedicationsContext';
import { PurpleChecked, PurpleUnchecked } from 'Images';
import { HeaderTitle } from './HeaderTitle';
import { NewUserInformation } from '../MedicationsAndPrescriptionsMain/NewUserInformation';
import { OrderPeriod } from './OrderPeriod';

export const SelectMedications = () => {
  const {
    checkedMedications,
    checkAll,
    checkAllMedications,
    unCheckAllMedications,
    addCheckedMedication,
    removeCheckedMedication,
    nextStep,
    outOfStockMedications,
  } = useContext(OrderRegularMedicationsContext);

  const { medications } = useMedications();
  const { prescriptionsAndUnHandledOrders } =
    usePrescriptionAndUnHandledOrders();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const urlParams = useParams();
  const isDesktop = useIsDesktop();
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [error, setError] = useState('');

  const handleToggle = useCallback(
    (medication: IMedication) => (e: React.MouseEvent) => {
      if (!isDesktop) e.stopPropagation();
      const isOutOfStock =
        medication.medstatus === MEDICATION_STATUSES.notAvailable;
      const list = isOutOfStock ? outOfStockMedications : checkedMedications;
      const prevIndex = list.findIndex(
        (prevMed) => prevMed.medicatioN_CODE === medication.medicatioN_CODE
      );

      if (prevIndex === -1) {
        addCheckedMedication(medication);
      } else {
        removeCheckedMedication(medication);
      }
    },
    [
      isDesktop,
      outOfStockMedications,
      checkedMedications,
      addCheckedMedication,
      removeCheckedMedication,
    ]
  );

  const handleNextStep = () => {
    if (checkedMedications.length === 0 && outOfStockMedications.length === 0) {
      setError(dictionary.noMedicationsSelectedError);
    } else {
      setError('');
      nextStep();
    }
  };

  const checkAllHandler = useCallback(() => {
    checkAllMedications(medications);
  }, [checkAllMedications, medications]);

  const checkAllCheckboxHandler = () => {
    checkAll ? unCheckAllMedications() : checkAllHandler();
  };

  const checkDuplicateOrderItems = useCallback(() => {
    const medicationsToCheck =
      prescriptionsAndUnHandledOrders?.prescriptions[0]?.medications;
    if (!medicationsToCheck) return;

    const matchingMedications = medicationsToCheck.flatMap((med: any) =>
      medications.filter(
        (origMed) => origMed.medicatioN_CODE === med.medicatioN_CODE
      )
    );

    if (matchingMedications.length > 0) {
      checkAllMedications(matchingMedications);
    }
  }, [prescriptionsAndUnHandledOrders, medications, checkAllMedications]);

  const headerTitle = urlParams.id
    ? `${dictionary.createOrder.title} - ${dictionary.duplicateOrder} ${urlParams.id}`
    : dictionary.createOrder.title;

  useEffect(() => {
    if (!isDesktop) {
      const handleScroll = () => {
        setScrolled(window.scrollY > 550);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (
      medications.length > 0 &&
      prescriptionsAndUnHandledOrders?.prescriptions?.length > 0
    ) {
      setIsLoading(false);
      if (urlParams.id) checkDuplicateOrderItems();
    }
  }, [
    medications,
    prescriptionsAndUnHandledOrders,
    urlParams.id,
    checkDuplicateOrderItems,
  ]);

  useEffect(() => {
    if (checkedMedications.length > 0 || outOfStockMedications.length > 0) {
      setError('');
    }
  }, [checkedMedications, outOfStockMedications]);

  if (isLoading && medications.length === 0) return <Loader />;
  if (medications.length === 0)
    return <NewUserInformation isNoValidrMedications={true} />;

  return (
    <>
      <CustomMetaTitle
        title={`${dictionary.title} - ${dictionary.createOrder.title}`}
      />
      <LinkReturnToMain
        label={dictionary.createOrder.breadcrumbs}
        link='/medicine'
        internal={true}
      />
      <HeaderTitle title={headerTitle} />
      <OrderPeriod />

      <Stack sx={{ maxWidth: '960px', pb: 2 }}>
        <Stack>
          <fieldset
            style={{
              padding: 0,
              margin: 0,
              marginBottom: '24px',
              border: 'none',
              maxHeight: 'calc(100% - 24px)',
            }}
          >
            <Container sx={isDesktop ? undefined : { p: 0 }}>
              <List sx={isDesktop ? undefined : { pb: 0 }}>
                <ListItem
                  sx={{
                    mt: isDesktop ? 3 : 0,
                    p: isDesktop ? undefined : '17px 7px',
                    height: isDesktop ? undefined : '64px',
                    pl: isDesktop ? 2 : 0,
                  }}
                >
                  <Stack
                    tabIndex={1}
                    direction='row'
                    alignItems='center'
                    p={isDesktop ? '4px 24px' : undefined}
                    onClick={checkAllCheckboxHandler}
                    sx={{ cursor: 'pointer', width: '45%' }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: isDesktop ? undefined : 'unset' }}
                    >
                      <Checkbox
                        icon={
                          <Avatar
                            alt=''
                            src={PurpleUnchecked}
                            sx={{ width: '24px', height: '24px' }}
                          />
                        }
                        checkedIcon={
                          <Avatar
                            alt=''
                            src={PurpleChecked}
                            sx={{ width: '24px', height: '24px' }}
                          />
                        }
                        sx={{
                          m: isDesktop ? undefined : 0,
                          height: isDesktop ? undefined : '18px',
                          width: isDesktop ? undefined : '18px',
                        }}
                        edge='start'
                        checked={checkAll}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ id: 'check-all' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <label
                          style={{
                            pointerEvents: 'none',
                            fontSize: isDesktop ? '24px' : undefined,
                            fontWeight: isDesktop ? 'bold' : undefined,
                            color: '#344266',
                          }}
                          htmlFor='check-all'
                        >
                          {dictionary.checkAll}
                        </label>
                      }
                      sx={{ ml: isDesktop ? undefined : '9px' }}
                    />
                  </Stack>

                  {isDesktop && (
                    <>
                      <Typography variant='body3' sx={{ width: '15%' }}>
                        {dictionary.units}
                      </Typography>
                      <Typography variant='body3' sx={{ width: '15%' }}>
                        {dictionary.dosageLabel}
                      </Typography>
                      <Typography variant='body3' sx={{ width: '20%' }}>
                        {dictionary.days}
                      </Typography>
                    </>
                  )}
                </ListItem>
                {medications.map((medication, index) => (
                  <MedicationListItem
                    key={medication.medicatioN_NAME}
                    checkedMedications={checkedMedications}
                    medication={medication}
                    outOfStockMedications={outOfStockMedications}
                    handleToggle={handleToggle}
                    showMedicationStatus={false}
                    index={index}
                  />
                ))}
              </List>
            </Container>
            {isDesktop && (
              <Typography color='#3A556C' ml='8px'>
                {checkedMedications.length + outOfStockMedications.length > 0
                  ? dictionary.youChoseNumberOfMedicationsDesktop(
                      checkedMedications.length + outOfStockMedications.length
                    )
                  : null}
              </Typography>
            )}
            {error && (
              <Typography role='alert' color='error' sx={{ mt: 2, ml: 2 }}>
                {error}
              </Typography>
            )}
          </fieldset>
        </Stack>
        <Container
          sx={{
            ...(isDesktop
              ? { display: 'flex', flexDirection: 'row-reverse' }
              : {
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }),
          }}
        >
          {!isDesktop && (
            <Stack
              sx={{
                backgroundColor: '#fff',
                mb: checkedMedications.length !== 0 ? '114px' : 10,
                position: scrolled ? 'fixed' : undefined,
                transition: 'transform 0.5s',
                transform: scrolled ? 'translateY(100%)' : 'translateY(0)',
                bottom: scrolled ? 0 : undefined,
                zIndex: 1300,
                gap: '10px',
                padding: '20px 0',
                alignItems: 'center',
              }}
            >
              {checkedMedications.length + outOfStockMedications.length > 0 &&
                dictionary.youChoseNumberOfMedicationsMobile(
                  checkedMedications.length + outOfStockMedications.length
                )}
              <NextPrevButton
                callback={handleNextStep}
                text={generalDictionary.continueToOrder}
                isBlue
                isNext
                style={{
                  button: {
                    mr: 2,
                    maxWidth: scrolled ? '200px' : undefined,
                    backgroundColor: 'primary.shade_400',
                    zIndex: 1,
                  },
                }}
              />
            </Stack>
          )}
          {isDesktop && (
            <NextPrevButton
              callback={handleNextStep}
              text={generalDictionary.continueToOrder}
              isBlue
              isNext
              style={{
                button: {
                  mr: 2,
                  maxWidth: scrolled ? '200px' : undefined,
                  backgroundColor: 'primary.shade_400',
                  zIndex: 1,
                },
              }}
            />
          )}
        </Container>
      </Stack>
    </>
  );
};
